import { FC, memo, useState } from "react";
import styled from "styled-components";
import JSZip from "jszip";
import * as htmlToImage from "html-to-image";

import { SectionHeading } from "@/components/Layout/Parts";
import ThumbnailsCheckbox from "./ThumbnailsCheckbox";
import Button from "@/components/Button";
import { hexToRgba } from "@/utils/theme";
import { ImageVariation } from "./ThumbnailsGenerator";
import { downloadZip } from "@/utils/helpers";

const GeneratedBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 16px;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 20px;
  img {
    width: 100%;
    height: 139px;
  }
`;

const StyledThumbnailsCheckbox = styled(ThumbnailsCheckbox)``;

const DownloadButton = styled.button`
  height: 24px;
  width: 24px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  position: absolute;
  bottom: 8px;
  left: 12px;
  color: black;
  display: flex;
  opacity: 0;
  justify-content: center;
  align-items: center;
  transition: opacity ease-out 0.2s;
`;

const ImageHolder = styled.div<{ $background: string }>`
  width: 100%;
  display: flex;
  position: relative;
  height: 139px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${(props) => props.$background});
  &:hover {
    ${StyledThumbnailsCheckbox} {
      opacity: 1;
    }
    ${DownloadButton} {
      opacity: 1;
    }
  }
`;

const StyledBreak = styled.div`
  width: 2px;
  margin-left: calc(50% - 1px);
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
`;

const Foreground = styled.img`
  position: absolute;
  left: 0;
`;

const HalfImgHolder = styled.div`
  width: 50%;
`;

const StyledHalfImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ButtonsHolder = styled.div`
  text-align: right;
  margin-top: 20px;
  margin-bottom: 56px;
  button {
    margin-left: 16px;
    &:disabled {
      background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
      border-color: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
`;

const SecondaryButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
    background-color: transparent;
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    &:hover {
      border-color: ${({ theme }) => theme.colors.grayscaleLight};
      color: ${({ theme }) => theme.colors.gray};
      background: ${({ theme }) => hexToRgba(theme.colors.black, 0.05)};
    }
  }
`;

interface Props {
  variations: ImageVariation[] | undefined;
  setCompletedGenerationsState: React.Dispatch<
    React.SetStateAction<{
      count: number;
      percentage: number;
    }>
  >;
  setShowProgress: React.Dispatch<React.SetStateAction<boolean>>;
}

const ThumbnailsGeneratedVariations: FC<Props> = memo(
  ({ variations, setCompletedGenerationsState, setShowProgress }) => {
    const [selectedImages, setSelectedImages] = useState<number[]>([]);

    const downloadSelected = async (
      shouldDownloadAll: boolean,
      currentImage?: ImageVariation
    ) => {
      if (!variations?.length) return;

      const holder = document.createElement("div");
      holder.classList.add("generated-images-holder");
      document.getElementById("root")!.appendChild(holder);

      if (currentImage) {
        const generatedImage = await generateImageVariation(
          currentImage,
          holder
        );

        downloadZip(generatedImage.image!, generatedImage.title!);

        holder.remove();

        return;
      }

      setCompletedGenerationsState({
        count: 1,
        percentage: 0,
      });

      setShowProgress(true);

      const foundImages = shouldDownloadAll
        ? variations
        : selectedImages.map(
            (selectedIndex: number) => variations[selectedIndex]
          );

      const zip = new JSZip();

      const generatedImages = await Promise.all(
        foundImages.map((image) => {
          return generateImageVariation(image, holder, foundImages.length);
        })
      );

      generatedImages.forEach((object) => {
        if (!object) return;
        zip.file(object.title!, object.image!, {
          binary: true,
        });
      });

      const content = await zip.generateAsync({ type: "blob" });

      downloadZip(content, "images.zip");

      holder.remove();

      setSelectedImages([]);

      setShowProgress(false);
    };

    const generateImageVariation = async (
      variation: ImageVariation,
      holderNode: HTMLDivElement,
      variationsCount?: number
    ) => {
      const imageHolder = document.createElement("div");
      imageHolder.classList.add("image-holder");

      if (variation.backgroundSrc) {
        const background = new Image();
        background.src = variation.backgroundSrc;
        background.classList.add("background");
        imageHolder.appendChild(background);
      }

      const middleBreak = document.createElement("div");
      middleBreak.classList.add("break");

      const leftImage = new Image();
      leftImage.src = variation.leftImageSrc;
      leftImage.classList.add("half-image");

      const rightImage = new Image();
      rightImage.src = variation.rightImageSrc;
      rightImage.classList.add("half-image");

      if (variation.foregroundSrc) {
        const foreground = new Image();
        foreground.src = variation.foregroundSrc;
        foreground.classList.add("foreground");
        imageHolder.appendChild(foreground);
      }

      imageHolder.appendChild(middleBreak);
      imageHolder.appendChild(leftImage);
      imageHolder.appendChild(rightImage);

      holderNode.appendChild(imageHolder);

      const image = await htmlToImage.toBlob(imageHolder);

      if (variationsCount) {
        setCompletedGenerationsState((prev) => {
          return {
            count: prev.count + 1,
            percentage: Math.round((prev.count / variationsCount) * 100),
          };
        });
      }

      return { image, title: variation.imageTitle };
    };

    return (
      <>
        <SectionHeading>Results</SectionHeading>
        <GeneratedBox>
          <GridLayout>
            {variations?.map((variation, index: number) => (
              <ImageHolder
                key={index}
                $background={variation.backgroundSrc ?? ""}
              >
                <StyledBreak />
                {variation.foregroundSrc && (
                  <Foreground src={variation.foregroundSrc} alt="" />
                )}
                <HalfImgHolder>
                  <StyledHalfImg src={variation.leftImageSrc} alt="" />
                </HalfImgHolder>
                <HalfImgHolder>
                  <StyledHalfImg src={variation.rightImageSrc} alt="" />
                </HalfImgHolder>
                <DownloadButton
                  onClick={() => downloadSelected(false, variation)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.9994 13.5385C11.0582 13.5385 13.5379 11.0588 13.5379 8C13.5379 4.94119 11.0582 2.46154 7.9994 2.46154C4.94059 2.46154 2.46094 4.94119 2.46094 8C2.46094 11.0588 4.94059 13.5385 7.9994 13.5385Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M6.04199 8.34956L8.00013 10.3077L9.95826 8.34956"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 5.69231V10.3077"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </DownloadButton>
                <StyledThumbnailsCheckbox
                  index={index}
                  isChecked={selectedImages.includes(index)}
                  setSelectedImages={setSelectedImages}
                />
              </ImageHolder>
            ))}
          </GridLayout>
        </GeneratedBox>
        <ButtonsHolder>
          <SecondaryButton onClick={() => downloadSelected(true)}>
            Download All
          </SecondaryButton>
          <Button
            disabled={!selectedImages.length}
            onClick={() => downloadSelected(false)}
          >
            Download Selected
          </Button>
        </ButtonsHolder>
      </>
    );
  }
);

export default ThumbnailsGeneratedVariations;
